import type {
  AgendaPublic,
  DisponibilitePublique,
  EtatSite,
  Horaires,
  MajSiteInput,
  RdvEnLigneInput,
  Site,
} from '@iobiz/shared'
import { api } from '../../api/client.js'

/**
 * Le site vitrine, côté écran.
 *
 * ⚠️ Trois familles d'appels qui ne se mélangent pas : ce que le GÉRANT écrit,
 * ce que la PLATEFORME publie, et ce que le PUBLIC lit. Les confondre dans un
 * seul module d'appels aurait fini par confondre les droits qui vont avec.
 */

// ── le gérant
export const lireSite = (storeId: string): Promise<EtatSite | null> =>
  api.get<EtatSite | null>(`/stores/${storeId}/site`)

export const enregistrerSite = (storeId: string, input: MajSiteInput): Promise<EtatSite> =>
  api.patch<EtatSite>(`/stores/${storeId}/site`, input)

// ── la plateforme
export const lireSiteAdmin = (storeId: string): Promise<EtatSite | null> =>
  api.get<EtatSite | null>(`/admin/stores/${storeId}/site`)

export const creerSiteAdmin = (storeId: string): Promise<EtatSite> =>
  api.post<EtatSite>(`/admin/stores/${storeId}/site`, {})

export const publierSiteAdmin = (storeId: string, publier: boolean): Promise<EtatSite> =>
  api.post<EtatSite>(`/admin/stores/${storeId}/site/publication`, { publier })

// ── le public
export interface SitePublic {
  boutique: {
    nom: string
    slug: string
    adresse: string | null
    telephone: string | null
    email: string | null
    logoUrl: string | null
    fuseau: string
  }
  contenu: Site
  rdvEnLigne: boolean
  horaires: Horaires
  prestations: ArticlePublic[]
  produits: ArticlePublic[]
}

export interface ArticlePublic {
  id: string
  nom: string
  description: string | null
  prix: string
  dureeMinutes: number | null
  image: string | null
  categorieId: string | null
}

/**
 * La PRÉVISUALISATION — le site avec son brouillon, réservée aux connectés.
 *
 * ⭐ Sans elle, l'administrateur ne découvre l'effet de ses modifications
 * qu'une fois le site public : il verrait son erreur en même temps que ses
 * clients.
 */
export const apercuVitrine = (storeId: string): Promise<SitePublic> =>
  api.get<SitePublic>(`/stores/${storeId}/site/apercu`)

export const lireVitrine = (slug: string): Promise<SitePublic> =>
  api.get<SitePublic>(`/public/boutiques/${slug}/site`, undefined, { sansAppareil: true })

/** L'agenda public d'un jour, poste par poste — la grille de la modale. */
export const agendaVitrine = (
  slug: string,
  itemId: string,
  jour: string,
): Promise<AgendaPublic> =>
  api.get<AgendaPublic>(
    `/public/boutiques/${slug}/agenda?itemId=${itemId}&jour=${jour}`,
    undefined,
    { sansAppareil: true },
  )

export const disponibilitesVitrine = (
  slug: string,
  itemId: string,
  jour: string,
): Promise<{ data: DisponibilitePublique[] }> =>
  api.get<{ data: DisponibilitePublique[] }>(
    `/public/boutiques/${slug}/disponibilites?itemId=${itemId}&jour=${jour}`,
    undefined,
    { sansAppareil: true },
  )

export const poserRdvVitrine = (
  slug: string,
  input: RdvEnLigneInput,
): Promise<{ debut: string; fin: string }> =>
  api.post<{ debut: string; fin: string }>(`/public/boutiques/${slug}/rendez-vous`, input, {
    sansAppareil: true,
  })
